import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clapperboard, Play } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";

import SearchField from "../../../../components/input/searchField/SearchField";
import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";
import FormModal from "../../../../components/modals/FormModal";
import ContentGrid from "../../../../components/grids/content/ContentGrid";
import VideoThumbnailCard from "../../../../components/cards/thumbnails/VideoThumbnailCard";

import CreatorVideoEditForm from "../forms/uploads/CreatorVideoEditForm";
import { useCreatorVideos } from "../hooks/useCreatorVideos";

import "./CreatorVideosView.css";

const LIMIT = 12;

const GRID_CONFIG = {
  columns: 4,
  tabletColumns: 3,
  mobileColumns: 2,
  gap: 16,
  showEmpty: false,
};

const isVideoProcessing = (video) => {
  const status = String(video?.hlsStatus || "")
    .trim()
    .toLowerCase();

  return ["pending", "processing"].includes(status);
};

export default function CreatorVideosView({ username }) {
  const navigate = useNavigate();

  const {
    videoThumbs = [],
    loading,
    error,
    getCreatorVideoThumbs,
    searchCreatorVideos,
    deleteCreatorVideo,
  } = useCreatorVideos(username);

  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const videos = Array.isArray(videoThumbs) ? videoThumbs : [];
  const hasVideos = videos.length > 0;
  const isBusy = loading || loadingMore;

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error?.message || "Unable to load videos."
    : "";

  useEffect(() => {
    let active = true;

    setOffset(0);
    setHasMore(false);

    if (!username) return;

    const loadVideos = async () => {
      try {
        const results = search
          ? await searchCreatorVideos({
              searchValue: search,
              limit: LIMIT,
              offset: 0,
            })
          : await getCreatorVideoThumbs(LIMIT, 0);

        if (!active) return;

        setHasMore(Array.isArray(results) && results.length === LIMIT);
      } catch (err) {
        if (active) {
          console.error("Failed to load videos:", err);
        }
      }
    };

    void loadVideos();

    return () => {
      active = false;
    };
  }, [username, search, refreshKey]);

  const handleSearchChange = (value) => {
    const next = typeof value === "string" ? value : value?.target?.value || "";

    setSearchTerm(next);

    if (!next.trim() && search) {
      setSearch("");
    }
  };

  const handleSearch = (event) => {
    event?.preventDefault?.();

    setSearch(searchTerm.replace(/,/g, "").trim());
  };

  const handleWatch = (video) => {
    if (!video?.videoTag || isVideoProcessing(video)) return;

    navigate(`/watch/${encodeURIComponent(video.videoTag)}`);
  };

  const handleLoadMore = async () => {
    if (isBusy || !hasMore) return;

    setLoadingMore(true);

    try {
      const nextOffset = offset + LIMIT;

      const results = search
        ? await searchCreatorVideos({
            searchValue: search,
            limit: LIMIT,
            offset: nextOffset,
          })
        : await getCreatorVideoThumbs(LIMIT, nextOffset);

      if (results === false) {
        setHasMore(false);
        return;
      }

      setOffset(nextOffset);

      setHasMore(Array.isArray(results) && results.length === LIMIT);
    } catch (err) {
      console.error("Failed to load more videos:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDelete = async (video) => {
    if (!video?.videoTag) return;

    if (!window.confirm("Delete this video?")) return;

    try {
      const result = await deleteCreatorVideo(video.videoTag);

      if (result === false || result?.success === false) {
        throw new Error("Unable to delete video.");
      }

      setRefreshKey((current) => current + 1);
    } catch (err) {
      console.error("Failed to delete video:", err);
      window.alert("Unable to delete this video.");
    }
  };

  const renderVideo = (video) => {
    const processing = isVideoProcessing(video);

    return (
      <div
        key={video.videoId ?? video.videoTag}
        className="creator-owner-videos__thumbnail"
      >
        <VideoThumbnailCard
          video={video}
          disabled={processing}
          onClick={() => handleWatch(video)}
          showMenuButton
          menuOptions={[
            {
              label: "Edit video",
              action: () => setEditingVideo(video),
            },
            {
              label: "Delete video",
              action: () => handleDelete(video),
            },
          ]}
        />

        {!processing && video.videoTag && (
          <div className="creator-owner-videos__play" aria-hidden="true">
            <Play size={22} fill="currentColor" />
          </div>
        )}
      </div>
    );
  };

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title="My Videos"
          subTitle="Manage and watch your uploaded videos."
        />

        <div className="creator-owner-videos__search">
          <SearchField
            value={searchTerm}
            onChange={handleSearchChange}
            onEnter={handleSearch}
            placeholder="Search videos..."
          />
        </div>

        {errorMessage && hasVideos && (
          <p className="creator-owner-videos__error" role="alert">
            {errorMessage}
          </p>
        )}

        {loading && !hasVideos ? (
          <ContentGrid
            {...GRID_CONFIG}
            items={[]}
            loading
            skeletonCount={LIMIT}
            renderItem={() => null}
          />
        ) : !hasVideos ? (
          <EmptyState
            icon={<Clapperboard size={28} />}
            title={
              errorMessage
                ? "Unable to load videos"
                : search
                  ? "No videos found"
                  : "No videos yet"
            }
            description={
              errorMessage ||
              (search
                ? "Try another search."
                : "Your uploaded videos will appear here.")
            }
            action={
              errorMessage ? (
                <Button
                  value="Try Again"
                  variant="primary-outlined"
                  onClick={() => setRefreshKey((value) => value + 1)}
                  disabled={isBusy}
                />
              ) : null
            }
          />
        ) : (
          <ContentGrid
            {...GRID_CONFIG}
            items={videos}
            renderItem={renderVideo}
          />
        )}

        {hasMore && hasVideos && (
          <div className="creator-owner-videos__footer">
            <Button
              value={loadingMore ? "Loading..." : "Load More"}
              variant="primary-outlined"
              disabled={isBusy}
              onClick={handleLoadMore}
            />
          </div>
        )}
      </ContentPanel>

      <FormModal
        isOpen={Boolean(editingVideo)}
        onClose={() => setEditingVideo(null)}
      >
        {editingVideo && (
          <CreatorVideoEditForm
            username={username}
            videoTag={editingVideo.videoTag}
            onClose={() => {
              setEditingVideo(null);
              setRefreshKey((value) => value + 1);
            }}
          />
        )}
      </FormModal>
    </ViewContainer>
  );
}
